import React, {useMemo} from 'react';
import './SlotRow.css';
import {IonItem, IonLabel, IonText} from "@ionic/react";
import {Company, ProfilePackage, Slot} from "../../../models";
import SlotBookingStatusChips from "../SlotBookingStatusChips";
import SlotAvailabilityStatusChips from "../SlotAvailabilityStatusChips";
import SlotAvailableLabel from "../SlotAvailableLabel";
import SlotButtons from "../SlotButtons";
import SlotHolidayLabel from "../SlotHolidayLabel";
import {useAppSelector} from "../../../redux/hooks";
import {bookingIdsFilteredSelector} from "../../../redux/bookings";
import {
    waitingBookingIdsFilteredSelector,
    waitingBookingOrderNumbersFilteredSelector
} from "../../../redux/waiting-bookings";

export interface SlotRowProps {
    actionsDisabled: boolean;
    profilePackage: ProfilePackage;
    company: Company;
    slot: Slot;

    onRowClicked: (slot: Slot) => void;
    onBookClicked: (slotId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onWaitClicked: (slotId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onCancelWaitClicked: (waitingBookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onDeleteClicked: (bookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onCancelClicked: (bookingId: number, lessonDateTs: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onUncancelClicked: (bookingId: number, lessonDateTs: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    onMoveClicked?: (bookingId: number, description: string, event: React.MouseEvent<HTMLIonButtonElement, MouseEvent>) => void;
    bookText?: string;
    moveMode?: boolean;
}

const SlotRow: React.FC<SlotRowProps> = (props) => {
    const {actionsDisabled, profilePackage, company, slot, onBookClicked, onWaitClicked, onCancelWaitClicked, onCancelClicked, onUncancelClicked, onDeleteClicked, onRowClicked, onMoveClicked, bookText, moveMode} = props;
    const selectorArgs = useMemo(() => ({profilePackageId: profilePackage.id, slots: [slot]}), [profilePackage.id, slot]);
    // visibleBookingIds is repeated in SlotModal
    const visibleBookingIds = useAppSelector((state) => bookingIdsFilteredSelector(state, selectorArgs ));
    // visibleWaitingBookingIds is repeated in SlotModal
    const visibleWaitingBookingIds = useAppSelector((state) => waitingBookingIdsFilteredSelector(state, selectorArgs ));
    // visibleWaitingBookingOrderNumbers is repeated in SlotModal
    const visibleWaitingBookingOrderNumbers = useAppSelector((state) => waitingBookingOrderNumbersFilteredSelector(state, selectorArgs ));
    // slotDescription is repeated in SlotModal
    const slotDescription = `${slot.dowAbbreviation} @ ${slot.startTimeDisplay} - ${slot.lessonDateDisplay}`;

    const slotWaitingOrderNumber = (visibleWaitingBookingOrderNumbers && visibleWaitingBookingOrderNumbers.hasOwnProperty(slot.id)
    && visibleWaitingBookingOrderNumbers[slot.id]) || undefined;

    return <IonItem className={`slot-row${slot.holidayLabel ? " slot-row-holiday" : ""}`} key={`profile-package-slot-${slot.id}`} onClick={() => {onRowClicked(slot)}}>
        <IonLabel>
            <>
                <IonText className="text-bold" key={`slot-${slot.id}-time`}>{`${slot.startTimeDisplay} - ${slot.endTimeDisplay} `}</IonText>
                {slot.lessonType && [<br key='br-lesson-type'/>, slot.lessonType.name]}
                {slot.facility && [<br key='br-facility'/>, <IonText className="text-bold" key={`slot-${slot.id}-at-text`}>@&nbsp;</IonText>, slot.facility.name]}
                <br key='br-by'/>
                <IonText className="text-bold" key={`slot-${slot.id}-by-text`}>by </IonText>
                {slot.employees && slot.employees.map((employee, iEmployee) => {
                    return <IonText key={`profile-package-slot-${slot.id}-employee-${employee.id}`}
                    >{employee.fullName}{iEmployee < slot.employees.length - 1 && `,`} </IonText>;
                })}
                {slot.holidayLabel && [<br key='br-holiday'/>, <SlotHolidayLabel key='holiday-label' label={slot.holidayLabel}/>]}
            </>
        </IonLabel>
        <>
            <SlotAvailableLabel slot={slot} className="slot-available ion-text-sm" waitingOrderNumber={slotWaitingOrderNumber} moveMode={moveMode} />
            <SlotButtons
                key={`slot-row-${slot.id}-buttons`}
                company={company}
                slot={slot}
                profilePackage={profilePackage}
                actionsDisabled={actionsDisabled}
                visibleBookingIds={visibleBookingIds}
                visibleWaitingBookingIds={visibleWaitingBookingIds}
                slotDescription={slotDescription}
                onCancelClicked={onCancelClicked}
                onUncancelClicked={onUncancelClicked}
                onDeleteClicked={onDeleteClicked}
                onWaitClicked={onWaitClicked}
                onCancelWaitClicked={onCancelWaitClicked}
                onBookClicked={onBookClicked}
                onMoveClicked={onMoveClicked}
                bookText={bookText}
                moveMode={moveMode}
            />
        </>
        <SlotAvailabilityStatusChips slot={slot} actionsDisabled={actionsDisabled} />
        <SlotBookingStatusChips slot={slot} actionsDisabled={actionsDisabled} />
    </IonItem>;
};

export default SlotRow;
